export function setupDocumentJsState() {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');
}

export function initializeCoreRuntime() {
  if (typeof document === 'undefined') {
    return;
  }

  const siteHeader = document.querySelector('.site-header');

  if (siteHeader) {
    let lastScrollY = window.scrollY || 0;
    let ticking = false;
    const scrollThreshold = 5;
    const scrollOffset = 200;

    const hasActivePinnedSections = () => document.querySelector(
      '.home-section--hero-pinned-active, .home-section--differentiators-pinned-active, .home-section--certifications-pinned-active'
    );
    const getHeaderHideSentinel = () => document.querySelector('[data-header-hide-sentinel]');
    const isHeaderHideBlockedBySentinel = () => {
      const sentinel = getHeaderHideSentinel();

      if (!sentinel) {
        return false;
      }

      const sentinelTop = sentinel.getBoundingClientRect().top;

      return sentinelTop > 0;
    };

    const syncHeaderVisibility = () => {
      const currentScrollY = window.scrollY || 0;
      const isMenuOpen = document.body.classList.contains('menu-open');
      const isPinnedSectionActive = !!hasActivePinnedSections();
      const isHideBlockedBySentinel = isHeaderHideBlockedBySentinel();
      siteHeader.classList.toggle('is-menu-open', isMenuOpen);

      if (currentScrollY <= scrollOffset || isMenuOpen || isHideBlockedBySentinel) {
        siteHeader.classList.remove('is-hidden-on-scroll');
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      if (isPinnedSectionActive) {
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollY;

      if (Math.abs(scrollDelta) < scrollThreshold) {
        ticking = false;
        return;
      }

      if (scrollDelta > 0) {
        siteHeader.classList.add('is-hidden-on-scroll');
      } else {
        siteHeader.classList.remove('is-hidden-on-scroll');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) {
        return;
      }

      ticking = true;
      window.requestAnimationFrame(syncHeaderVisibility);
    }, { passive: true });

    window.addEventListener('resize', () => {
      siteHeader.classList.remove('is-hidden-on-scroll');
      lastScrollY = window.scrollY || 0;
    });

    syncHeaderVisibility();
  }

  const initializeAboutPartnersBridge = () => {
    const bridgeGroup = document.querySelector('.our-partners-group--bridged');

    if (!bridgeGroup) {
      return;
    }

    const svg = bridgeGroup.querySelector('.about-partners-bridge__svg');
    const allPaths = Array.from(bridgeGroup.querySelectorAll('.about-partners-bridge__path'));

    if (!allPaths.length || !svg) {
      return;
    }

    // Draw order: path1 reversed (1c→1a), path3 reversed (3c→3a), path2 reversed (2c→2a).
    const orderedPaths = [allPaths[0], allPaths[2], allPaths[1]];
    const lengths = orderedPaths.map((p) => p.getTotalLength());
    const totalLength = lengths.reduce((a, b) => a + b, 0);
    const totalDuration = 2600;

    // Hide each path at its end point (negative offset = draw reversed).
    orderedPaths.forEach((path, i) => {
      path.style.transition = 'none';
      path.style.strokeDasharray = lengths[i];
      path.style.strokeDashoffset = -lengths[i];
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      orderedPaths.forEach((path) => {
        path.style.strokeDashoffset = '0';
      });
      return;
    }

    const drawBridge = () => {
      let delay = 200;
      orderedPaths.forEach((path, i) => {
        const segDuration = Math.round((lengths[i] / totalLength) * totalDuration);
        setTimeout(() => {
          path.style.transition = `stroke-dashoffset ${segDuration}ms linear`;
          path.style.strokeDashoffset = '0';
        }, delay);
        delay += segDuration;
      });
    };

    if (typeof IntersectionObserver !== 'function') {
      requestAnimationFrame(drawBridge);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          drawBridge();
          observer.disconnect();
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.15,
      }
    );

    observer.observe(bridgeGroup);
  };

  initializeAboutPartnersBridge();

  const initialize404Squiggles = () => {
    const svg1 = document.querySelector('.rs-404-squiggle-one__svg');
    const svg2 = document.querySelector('.rs-404-squiggle-two__svg');
    if (!svg1 && !svg2) return;

    const paths = [
      svg1 && svg1.querySelector('.rs-404-squiggle-one__path'),
      svg2 && svg2.querySelector('.rs-404-squiggle-two__path'),
    ].filter(Boolean);
    if (!paths.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    paths.forEach((p) => {
      const len = p.getTotalLength();
      p.style.transition = 'none';
      p.style.strokeDasharray = String(len);
      p.style.strokeDashoffset = reduced ? '0' : String(len);
    });

    if (reduced) return;

    const draw = () => {
      setTimeout(() => {
        paths.forEach((p) => {
          p.style.transition = 'stroke-dashoffset 3000ms linear';
          p.style.strokeDashoffset = '0';
        });
      }, 200);
    };

    if (typeof IntersectionObserver !== 'function') {
      requestAnimationFrame(draw);
      return;
    }

    const hero = document.querySelector('.rs-404__hero');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          draw();
          observer.disconnect();
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
    observer.observe(hero || paths[0].closest('svg'));
  };

  initialize404Squiggles();

  const initializeEventsHereSquiggle = () => {
    const sections = Array.from(document.querySelectorAll('.home-section--events-here'));
    if (!sections.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const play = (path, len) => {
      // Re-prime on trigger so the draw always starts from a hidden stroke.
      path.style.transition = 'none';
      path.style.strokeDasharray = String(len);
      path.style.strokeDashoffset = String(len);

      requestAnimationFrame(() => {
        // Force style flush so the browser animates from the primed dash state.
        path.getBoundingClientRect();
        path.style.transition = 'stroke-dashoffset 3000ms linear';
        path.style.strokeDashoffset = '0';
      });
    };

    sections.forEach((section) => {
      const path = section.querySelector('.rs-events-here-squiggle__path');
      if (!path || typeof path.getTotalLength !== 'function') return;

      const len = path.getTotalLength();
      if (!len || !isFinite(len)) return;

      path.style.transition = 'none';
      path.style.strokeDasharray = String(len);
      path.style.strokeDashoffset = reduced ? '0' : String(len);

      if (reduced) return;

      if (typeof IntersectionObserver !== 'function') {
        play(path, len);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            play(path, len);
            observer.unobserve(section);
            observer.disconnect();
          });
        },
        { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
      );

      observer.observe(section);
    });
  };

  initializeEventsHereSquiggle();

  const initializeLocationDetailsSquiggle = () => {
    const svg = document.querySelector('.rs-location-details-squiggle__svg');
    if (!svg) return;
    const path = svg.querySelector('.rs-location-details-squiggle__path');
    if (!path) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const len = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = String(len);
    path.style.strokeDashoffset = reduced ? '0' : String(len);

    if (reduced) return;

    const draw = () => {
      setTimeout(() => {
        path.style.transition = 'stroke-dashoffset 3000ms linear';
        path.style.strokeDashoffset = '0';
      }, 200);
    };

    if (typeof IntersectionObserver !== 'function') {
      requestAnimationFrame(draw);
      return;
    }

    const section = svg.closest('section') || svg;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          draw();
          observer.disconnect();
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
    observer.observe(section);
  };

  initializeLocationDetailsSquiggle();

  const initializeOurServicesPartnersBridge = () => {
    const svg = document.querySelector('.our-services-partners-bridge__svg');
    if (!svg) return;
    const path = svg.querySelector('.our-services-partners-bridge__path');
    if (!path) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const len = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = String(len);
    path.style.strokeDashoffset = reduced ? '0' : String(len);

    if (reduced) return;

    const draw = () => {
      setTimeout(() => {
        path.style.transition = 'stroke-dashoffset 3000ms linear';
        path.style.strokeDashoffset = '0';
      }, 200);
    };

    if (typeof IntersectionObserver !== 'function') {
      requestAnimationFrame(draw);
      return;
    }

    const group = svg.closest('.our-services-partners-group--bridged') || svg;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          draw();
          observer.disconnect();
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
    observer.observe(group);
  };

  initializeOurServicesPartnersBridge();

  const initializeCertificationsSquiggle = () => {
    const svg = document.querySelector('.certifications__squiggle-svg');
    if (!svg) return;
    const path = svg.querySelector('.certifications__squiggle-path');
    if (!path) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const len = path.getTotalLength();
    path.style.transition = 'none';
    path.style.strokeDasharray = String(len);
    path.style.strokeDashoffset = reduced ? '0' : String(len);

    if (reduced) return;

    const draw = () => {
      setTimeout(() => {
        path.style.transition = 'stroke-dashoffset 3000ms linear';
        path.style.strokeDashoffset = '0';
      }, 200);
    };

    if (typeof IntersectionObserver !== 'function') {
      requestAnimationFrame(draw);
      return;
    }

    const section = svg.closest('section') || svg;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          draw();
          observer.disconnect();
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
    observer.observe(section);
  };

  initializeCertificationsSquiggle();
  const menuToggle = document.querySelector('.mobile-menu-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');
  const menuOverlay = document.querySelector('.mobile-menu-overlay');
  const mobileMenuSquigglePath = document.querySelector('.mobile-menu-squiggle__path');
  const mobileMenuSquiggleLine1Path = document.querySelector('.mobile-menu-squiggle--line-1__path');
  const mobileMenuSquiggleLine3Path = document.querySelector('.mobile-menu-squiggle--line-3__path');
  const mobileMenuFocusSelector = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  let lastFocusedElement = null;
  let mobileMenuSquiggleLength = 8000;
  let mobileMenuSquiggleLine1Length = 8000;
  let mobileMenuSquiggleLine3Length = 8000;

  const getMenuFocusableElements = () =>
    Array.from(mobileMenu.querySelectorAll(mobileMenuFocusSelector)).filter(
      (element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
    );

  if (!menuToggle || !mobileMenu || !menuOverlay) {
    return;
  }

  if (mobileMenuSquigglePath && typeof mobileMenuSquigglePath.getTotalLength === 'function') {
    mobileMenuSquiggleLength = mobileMenuSquigglePath.getTotalLength();
  }

  if (mobileMenuSquigglePath) {
    mobileMenuSquigglePath.style.setProperty('--mobile-menu-squiggle-length', String(mobileMenuSquiggleLength));
    mobileMenuSquigglePath.style.strokeDashoffset = String(mobileMenuSquiggleLength);
  }

  if (mobileMenuSquiggleLine1Path && typeof mobileMenuSquiggleLine1Path.getTotalLength === 'function') {
    mobileMenuSquiggleLine1Length = mobileMenuSquiggleLine1Path.getTotalLength();
  }
  if (mobileMenuSquiggleLine1Path) {
    mobileMenuSquiggleLine1Path.style.setProperty('--mobile-menu-squiggle-line-1-length', String(mobileMenuSquiggleLine1Length));
    mobileMenuSquiggleLine1Path.style.strokeDashoffset = String(mobileMenuSquiggleLine1Length);
  }

  if (mobileMenuSquiggleLine3Path && typeof mobileMenuSquiggleLine3Path.getTotalLength === 'function') {
    mobileMenuSquiggleLine3Length = mobileMenuSquiggleLine3Path.getTotalLength();
  }
  if (mobileMenuSquiggleLine3Path) {
    mobileMenuSquiggleLine3Path.style.setProperty('--mobile-menu-squiggle-line-3-length', String(mobileMenuSquiggleLine3Length));
    mobileMenuSquiggleLine3Path.style.strokeDashoffset = String(mobileMenuSquiggleLine3Length);
  }

  const setMenuState = (isOpen) => {
    menuToggle.classList.toggle('is-open', isOpen);
    mobileMenu.classList.toggle('is-open', isOpen);
    menuOverlay.classList.toggle('is-open', isOpen);
    menuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    mobileMenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    document.body.classList.toggle('menu-open', isOpen);
    if (siteHeader) {
      siteHeader.classList.toggle('is-menu-open', isOpen);
      if (isOpen) {
        siteHeader.classList.remove('is-hidden-on-scroll');
      }
    }

    const squigglePaths = [
      { path: mobileMenuSquigglePath, length: mobileMenuSquiggleLength },
      { path: mobileMenuSquiggleLine1Path, length: mobileMenuSquiggleLine1Length },
      { path: mobileMenuSquiggleLine3Path, length: mobileMenuSquiggleLine3Length },
    ];

    if (isOpen) {
      const activeIndex = Math.floor(Math.random() * squigglePaths.length);
      squigglePaths.forEach(function (item, index) {
        if (!item.path) return;
        if (index === activeIndex) {
          item.path.style.animation = 'none';
          void item.path.getBoundingClientRect();
          item.path.style.animation = '';
        } else {
          item.path.style.animation = 'none';
          item.path.style.strokeDashoffset = String(item.length);
        }
      });
    } else {
      squigglePaths.forEach(function (item) {
        if (!item.path) return;
        item.path.style.strokeDashoffset = String(item.length);
      });
    }

    if (isOpen) {
      lastFocusedElement = document.activeElement;

      if (typeof mobileMenu.focus === 'function') {
        mobileMenu.focus({ preventScroll: true });
      }

      menuToggle.setAttribute('aria-label', 'Close mobile menu');
      return;
    }

    menuToggle.setAttribute('aria-label', 'Open mobile menu');

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  };

  menuToggle.setAttribute('aria-label', 'Open mobile menu');

  menuToggle.addEventListener('click', () => {
    const nextState = menuToggle.getAttribute('aria-expanded') !== 'true';
    setMenuState(nextState);
  });

  menuOverlay.addEventListener('click', () => {
    setMenuState(false);
  });

  mobileMenu.addEventListener('click', (event) => {
    const link = event.target.closest('a');

    if (link) {
      setMenuState(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    const isMenuOpen = menuToggle.getAttribute('aria-expanded') === 'true';

    if (event.key === 'Escape') {
      setMenuState(false);
      return;
    }

    if (!isMenuOpen || event.key !== 'Tab') {
      return;
    }

    const focusable = getMenuFocusableElements();

    if (focusable.length === 0) {
      event.preventDefault();
      menuToggle.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && activeElement === mobileMenu) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setMenuState(false);
    }
  });
}

export function waitForInitialResources() {
  if (typeof document === 'undefined' || document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}
